import React, { createContext, useState, useCallback, useRef, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { deepMerge } from '../utils/deepMerge';
import debounce from 'lodash.debounce';
import { useAuth } from '../hooks/useAuth';
import { isModularModule } from '../constants/assessmentModules';


export const PatientSelfDataContext = createContext();

export const PatientSelfDataProvider = ({ children }) => {
    // This context is intended ONLY for service users (patients/informants).
    // It loads the authenticated service user's own document from the `patients`
    // collection via a one-shot getDoc on mount, mirroring the provider-side
    // PatientDataContext pattern. We deliberately avoid onSnapshot because
    // real-time listeners create new React state references on every Firestore
    // write, which cascades through useMemo/useEffect chains and causes
    // destructive form resets on slow networks (see patient_form_reset_bug plan).
    // Providers (clinicians/admin/support) should never load data here; they
    // access patient data via PatientDataContext.

    const { user: currentUser, claims } = useAuth();

    // Only Assessment service users (Patient / Informant) should subscribe
    // to their own `patients/{uid}` document. RMS service users (RMS School
    // Representative / RMS Legal Guardian) have `accountType: 'RMS Service User'`
    // and store their data under `organisations/{orgId}/patientRecords/...`
    // instead — they naturally don't match this check.
    const isServiceUserAccount = claims?.accountType === 'Service User';

    // State management for patient's own data
    const [patientDataModel, setPatientDataModel] = useState(() => {
      const storedData = localStorage.getItem('patientDataModel');
      return storedData ? JSON.parse(storedData) : null;
    });

  
    // Direct fetch of patient's own document
    const fetchPatientData = useCallback(async () => {
      try {
        // Providers should never fetch their own record from `patients`
        if (!isServiceUserAccount) {
          return;
        }

        // Informants on modular pathways (Adult Autism, CYP ADHD, ...) live in
        // the informants/ collection, not patients/. Their data is managed by
        // ModularInformantSelfDataContext instead. Checking the module registry
        // rather than 'adultAutism' specifically stops a CYP ADHD informant
        // issuing a doomed read against patients/{their own uid}.
        if (claims?.userType === 'Informant' && isModularModule(claims?.assessmentModule)) {
          return;
        }

        const user = currentUser;
        if (!user) {
          console.warn("No authenticated user found.");
          return;
        }
  
        const patientDocRef = doc(db, 'patients', user.uid);
        const patientDoc = await getDoc(patientDocRef);
        
        if (!patientDoc.exists()) {
          console.warn(`Patient document not found for user ID: ${user.uid}`);
          return;
        }
        
        const patientData = patientDoc.data();
        setPatientDataModel(patientData);
        localStorage.setItem('patientDataModel', JSON.stringify(patientData));
        // console.log('Patient Data Model:', patientData);
    
      } catch (error) {
        console.error("Error fetching patient data: ", error);
      }
    }, [currentUser, isServiceUserAccount, claims]);
  
    // Load the patient's own document on mount (and when auth changes).
    // No real-time listener -- see provider comment above for rationale.
    useEffect(() => {
      if (!isServiceUserAccount || !currentUser) {
        setPatientDataModel(null);
        localStorage.removeItem('patientDataModel');
        return;
      }
      fetchPatientData();
    }, [currentUser, isServiceUserAccount, fetchPatientData]);
  
  // Queue for batching updates
  const updateQueue = useRef([]);

  // Debounced processing of updates
  const debouncedProcessQueue = useRef(debounce(async () => {
    if (updateQueue.current.length === 0) return Promise.resolve();
  
    try {
      // Only service users can write to their own document
      if (!isServiceUserAccount) {
        updateQueue.current = [];
        return Promise.resolve();
      }

      const user = currentUser;
      if (!user) throw new Error("No authenticated user found");
  
      const patientDocRef = doc(db, 'patients', user.uid);
      const patientDoc = await getDoc(patientDocRef);
  
      if (!patientDoc.exists()) {
        throw new Error(`Patient document not found for user ID: ${user.uid}`);
      }
  
      const updatedPatient = { ...patientDoc.data() };
      updateQueue.current.forEach(update => {
        updatedPatient.formData = deepMerge(updatedPatient.formData, update);
      });
  
      await updateDoc(patientDocRef, updatedPatient);
  
      setPatientDataModel(updatedPatient);
      localStorage.setItem('patientDataModel', JSON.stringify(updatedPatient));
      console.log('Updated patient data model:', updateQueue.current);
  
      updateQueue.current = [];
      return Promise.resolve();
    } catch (error) {
      console.error("Error updating patient data model: ", error);
      return Promise.reject(error);
    }
  }, 2000)).current;

  // Function to add updates to queue
  const updatePatientData = useCallback((updates) => {
    return new Promise((resolve, reject) => {
      try {
        updateQueue.current.push(updates);
        
        Promise.resolve(debouncedProcessQueue())
          .then(() => resolve())
          .catch((error) => reject(error));
      } catch (error) {
        reject(error);
      }
    });
  }, [debouncedProcessQueue]);

  // Clear patient data
  const clearPatientData = useCallback(() => {
    setPatientDataModel(null);
    localStorage.removeItem('patientDataModel');
  }, []);

  // Alias for backward compatibility and semantic clarity
  const refreshPatientData = fetchPatientData;

  return (
    <PatientSelfDataContext.Provider 
      value={{ 
        patientDataModel, 
        fetchPatientData, 
        refreshPatientData,
        clearPatientData, 
        updatePatientData 
      }}
    >
      {children}
    </PatientSelfDataContext.Provider>
  );
};